import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { VehicleForm } from "@/components/VehicleForm";
import { api, formatApiError, STATUS_META, STATUS_ORDER, brl, fmtExpected, isExpectedOverdue, fromLocalInput } from "@/lib/api";
import { Money, HideValuesToggle } from "@/components/HideValues";
import { StatusBadge, StatusTimeline } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Car, Loader2, UserPlus, Camera, ShieldCheck, X, CheckCircle2, ArrowRight, CalendarClock, MessageCircle, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useWaMsg, vehicleShort } from "@/lib/waTemplates";
import { compressImage } from "@/lib/draft";
import { PeriodPicker, PERIOD_RANGES } from "@/components/PeriodPicker";

const INTAKE_SLOT_LABELS = ["Traseira", "Frente", "Lado esquerdo", "Lado direito", "Interior"];
const MAX_INTAKE_SLOTS = INTAKE_SLOT_LABELS.length;

const empty = {
  customer_id: "",
  description: "",
  expected_at: "",
  vehicle: { make: "", model: "", year: "", year_model: "", plate: "", color: "", chassi: "" },
};

export default function OwnerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const waMsg = useWaMsg();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todos");
  const [search, setSearch] = useState("");
  const [preset, setPreset] = useState("month");
  const monthStartIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
  const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(todayIso());
  const setRange = ([f, t]) => { setFrom(f); setTo(t); };
  const [customers, setCustomers] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => { const s = params.get("status"); if (s && STATUS_META[s]) { setFilter(s); } }, [params]);
  useEffect(() => { if (params.get("new") === "1") { navigate("/owner/nova-ordem", { replace: true }); } }, [params, navigate]);
  const [form, setForm] = useState(empty);
  const [intakeFiles, setIntakeFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const intakeInputRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const [o, c, u] = await Promise.all([
        api.get("/orders"),
        api.get("/customers"),
        api.get("/orders/upcoming-revisions").catch(() => ({ data: [] })),
      ]);
      setOrders(o.data);
      setCustomers(c.data);
      setUpcoming(u.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const inRange = (iso) => { if (!iso) return false; const d = String(iso).slice(0, 10); return d >= from && d <= to; };
  const scopedOrders = useMemo(() => orders.filter((o) => inRange(o.created_at)), [orders, from, to]);

  const metrics = useMemo(() => {
    const by = {};
    scopedOrders.forEach((o) => (by[o.status] = (by[o.status] || 0) + 1));
    return by;
  }, [scopedOrders]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return scopedOrders.filter((o) => {
      if (filter !== "todos" && o.status !== filter) return false;
      if (!s) return true;
      const hay = `${o.customer_name || ""} ${o.vehicle?.plate || ""} ${o.vehicle?.make || ""} ${o.vehicle?.model || ""}`.toLowerCase();
      return hay.includes(s);
    });
  }, [scopedOrders, filter, search]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        description: form.description,
        expected_at: fromLocalInput(form.expected_at),
        vehicle: {
          make: form.vehicle.make,
          model: form.vehicle.model,
          plate: form.vehicle.plate,
          color: form.vehicle.color || null,
          chassi: (form.vehicle.chassi || "").trim() || null,
          year: form.vehicle.year === "" ? null : Number(form.vehicle.year),
          year_model: form.vehicle.year_model === "" ? null : Number(form.vehicle.year_model),
        },
      };
      const { data } = await api.post("/orders", payload);
      if (intakeFiles.length > 0) {
        try {
          for (const f of intakeFiles) {
            const fd = new FormData();
            fd.append("file", await compressImage(f));
            await api.post(`/orders/${data.id}/photos?category=intake`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          }
          toast.success(`Ordem criada com ${intakeFiles.length} foto${intakeFiles.length === 1 ? "" : "s"} de entrada`);
        } catch (upErr) {
          toast.error("Ordem criada, mas falhou ao enviar algumas fotos de entrada");
        }
      } else {
        toast.success("Ordem de serviço criada");
      }
      setOrders((p) => [data, ...p]);
      setForm(empty);
      setIntakeFiles([]);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">
            Ordens de serviço
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Gerencie veículos, status e checklist em tempo real.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HideValuesToggle />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild onClick={(e) => { e.preventDefault(); navigate("/owner/nova-ordem"); }}>
            <Button
              data-testid="new-order-btn"
              disabled={user?.shop_id === "all"}
              title={user?.shop_id === "all" ? "Selecione uma unidade específica para criar uma ordem" : undefined}
              className="w-full sm:w-auto h-12 px-6 rounded-2xl bg-gradient-to-b from-green-neon to-green-brandDark text-zinc-950 font-bold uppercase tracking-wide shadow-[0_8px_28px_-10px_rgba(74,222,128,0.55)] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-5 w-5 mr-2" /> {user?.shop_id === "all" ? "Escolha uma unidade" : "Nova Ordem"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg p-0">
            <div className="p-6 pb-2">
              <DialogHeader>
                <DialogTitle className="font-display uppercase">Nova ordem de serviço</DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Vincule um cliente e cadastre o veículo que será atendido.
                </DialogDescription>
              </DialogHeader>
            </div>
            <form onSubmit={submit} className="space-y-3 px-6 pb-24" data-testid="new-order-form">
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Cliente</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm((p) => ({ ...p, customer_id: v }))}>
                  <SelectTrigger
                    data-testid="order-customer-select"
                    className="mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100"
                  >
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {customers.length === 0 && (
                      <div className="px-3 py-4 text-xs text-zinc-500 flex items-center gap-2">
                        <UserPlus className="h-4 w-4" />
                        Nenhum cliente cadastrado.
                      </div>
                    )}
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`customer-opt-${c.id}`}>
                        {c.name} — {c.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <VehicleForm value={form.vehicle} onChange={(v) => setForm((p) => ({ ...p, vehicle: v }))} idPrefix="order-vehicle" />
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Descrição do serviço</Label>
                <Input data-testid="order-description" required value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800" placeholder="Revisão de 20.000 km" />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Previsão de entrega (opcional)</Label>
                <Input data-testid="order-expected-input" type="datetime-local" value={form.expected_at}
                  onChange={(e) => setForm((p) => ({ ...p, expected_at: e.target.value }))}
                  style={{ WebkitAppearance: "none" }}
                  className="mt-1 bg-zinc-900 border-zinc-800 w-full min-w-0 max-w-full block" />
              </div>
              <div className="rounded-lg border border-dashed border-amber-brand/40 bg-amber-brand/5 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-amber-brand" />
                    <Label className="text-xs font-mono-caps text-amber-200">Fotos de entrada (opcional)</Label>
                  </div>
                  <span className="text-[10px] text-amber-200/70">{intakeFiles.length}/{MAX_INTAKE_SLOTS}</span>
                </div>
                <p className="text-[11px] text-zinc-400 mb-2">
                  Registre o estado do carro ao chegar — até {MAX_INTAKE_SLOTS} fotos: traseira, frente, laterais e interior.
                </p>
                <div className="grid grid-cols-5 gap-2 mb-2">
                  {Array.from({ length: MAX_INTAKE_SLOTS }).map((_, idx) => {
                    const f = intakeFiles[idx];
                    const url = f ? URL.createObjectURL(f) : null;
                    return (
                      <div
                        key={idx}
                        data-testid={`intake-slot-${idx}`}
                        className="relative aspect-square rounded-md border border-zinc-800 bg-zinc-900 overflow-hidden flex items-center justify-center"
                      >
                        {url ? (
                          <>
                            <img src={url} alt={INTAKE_SLOT_LABELS[idx]} className="h-full w-full object-cover" />
                            <button
                              type="button"
                              data-testid={`intake-remove-${idx}`}
                              onClick={() => setIntakeFiles((prev) => prev.filter((_, i) => i !== idx))}
                              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/80 border border-zinc-700 text-zinc-200 flex items-center justify-center"
                              aria-label="Remover foto"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </>
                        ) : (
                          <div className="text-[9px] text-zinc-600 text-center px-1">{INTAKE_SLOT_LABELS[idx]}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <input
                  id="order-intake-input"
                  data-testid="order-intake-input"
                  ref={intakeInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const added = Array.from(e.target.files || []);
                    if (!added.length) return;
                    let overflow = false;
                    setIntakeFiles((prev) => {
                      overflow = prev.length + added.length > MAX_INTAKE_SLOTS;
                      return [...prev, ...added].slice(0, MAX_INTAKE_SLOTS);
                    });
                    if (overflow) toast.info(`Máximo de ${MAX_INTAKE_SLOTS} fotos de entrada`);
                    if (intakeInputRef.current) intakeInputRef.current.value = "";
                  }}
                />
                <Button
                  type="button"
                  data-testid="order-intake-pick"
                  variant="outline"
                  disabled={intakeFiles.length >= MAX_INTAKE_SLOTS}
                  onClick={() => intakeInputRef.current?.click()}
                  className="w-full h-9 bg-zinc-900 border-amber-brand/40 text-amber-200 hover:bg-amber-brand/10 hover:text-amber-100 disabled:opacity-50"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  {intakeFiles.length === 0 ? "Adicionar fotos" : "Adicionar mais"}
                </Button>
              </div>
              <div className="sticky bottom-0 -mx-6 px-6 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] bg-zinc-950 border-t border-zinc-900">
                <Button
                  data-testid="order-submit-btn"
                  type="submit"
                  disabled={saving || !form.customer_id}
                  className="w-full h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar ordem"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <PeriodPicker testid="orders-period" preset={preset} setPreset={setPreset} from={from} to={to} setRange={setRange} />

      <div className="grid grid-cols-2 gap-3">
        {STATUS_ORDER.filter((s) => s !== "pronto").map((s) => {
          const m = STATUS_META[s];
          const count = metrics[s] || 0;
          return (
            <div
              key={s}
              data-testid={`metric-${s}`}
              className="rounded-2xl border border-card2-border bg-card2 p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className={`h-2 w-2 rounded-full ${m.dot}`} />
                <div className="text-[11px] font-mono-caps text-zinc-400 leading-tight">{m.cardTitle}</div>
              </div>
              <div className="flex items-baseline gap-2">
                <div data-testid={`metric-value-${s}`} className="font-display text-5xl font-black text-zinc-100 leading-none">
                  {String(count).padStart(2, "0")}
                </div>
                <div className="text-[11px] text-zinc-500 leading-tight">{m.subtitle}</div>
              </div>
            </div>
          );
        })}
      </div>

      <Link
        to="#orders"
        onClick={(e) => { e.preventDefault(); setFilter("pronto"); document.getElementById("orders-section")?.scrollIntoView({ behavior: "smooth" }); }}
        data-testid="metric-pronto"
        className="flex items-center gap-4 rounded-2xl border border-green-brand/50 bg-green-brand/10 p-4 hover:bg-green-brand/15 transition-colors"
      >
        <div className="h-11 w-11 rounded-full bg-green-brand/20 border border-green-brand/50 flex items-center justify-center shrink-0">
          <CheckCircle2 className="h-6 w-6 text-green-neon" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-zinc-100">Prontos para retirada</div>
          <div className="text-xs text-green-300">Cliente já avisado</div>
        </div>
        <div data-testid="metric-value-pronto" className="font-display text-4xl font-black text-green-neon leading-none">
          {String(metrics.pronto || 0).padStart(2, "0")}
        </div>
      </Link>

      {upcoming.length > 0 && (
        <section data-testid="upcoming-revisions" className="rounded-2xl border border-amber-brand/40 bg-amber-brand/5 p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-amber-300" />
              <h2 className="font-display text-sm font-bold uppercase tracking-wide text-zinc-100">
                Revisões próximas
              </h2>
            </div>
            <span className="text-[11px] font-mono-caps text-amber-200">próximos 7 dias · {upcoming.length}</span>
          </div>
          <ul className="space-y-2">
            {upcoming.map((u) => {
              const digits = (u.customer_phone || "").replace(/\D/g, "");
              const wa = digits ? (digits.length >= 12 ? digits : `55${digits}`) : "";
              const plate = u.vehicle?.plate || "—";
              const dateIso = u?.next_revision?.date || "";
              const km = u?.next_revision?.km;
              let dateLabel = dateIso;
              try { if (dateIso) dateLabel = new Date(dateIso + "T00:00:00").toLocaleDateString("pt-BR"); } catch {}
              const msg = encodeURIComponent(waMsg("revision", {
                cliente: u.customer_name || "", placa: plate, modelo: vehicleShort(u.vehicle),
                data: `${dateLabel}${km != null ? ` ou aos ${Number(km).toLocaleString("pt-BR")} km` : ""}`,
              }));
              const href = wa ? `https://wa.me/${wa}?text=${msg}` : "";
              return (
                <li key={u.id} data-testid={`upcoming-${u.id}`} className="flex items-center gap-3 rounded-xl border border-card2-border bg-card2 p-3">
                  <Link to={`/owner/ordens/${u.id}`} className="flex-1 min-w-0 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl border border-amber-brand/40 bg-amber-brand/15 flex items-center justify-center shrink-0">
                      <CalendarClock className="h-5 w-5 text-amber-300" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-zinc-100 truncate">{u.customer_name || "—"}</div>
                      <div className="text-[11px] text-zinc-400 truncate" translate="no">
                        <span className="font-semibold">{plate}</span>
                        {dateIso && <span> · {dateLabel}</span>}
                        {km != null && <span> · {Number(km).toLocaleString("pt-BR")} km</span>}
                      </div>
                    </div>
                  </Link>
                  {wa && (
                    <a
                      data-testid={`upcoming-wa-${u.id}`}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Avisar no WhatsApp"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wide shrink-0"
                    >
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div id="orders-section" className="flex flex-col gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input data-testid="order-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar placa, cliente ou veículo..."
            className="pl-10 h-12 rounded-2xl bg-card2 border-card2-border text-zinc-100 placeholder:text-zinc-500" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin -mx-1 px-1">
            {[{ k: "todos", label: "Todos" }, ...STATUS_ORDER.map((k) => ({ k, label: STATUS_META[k].short }))].map(({ k, label }) => (
              <button
                key={k}
                data-testid={`filter-${k}`}
                onClick={() => setFilter(k)}
                className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold tracking-wide border transition-colors ${
                  filter === k
                    ? "bg-green-brand text-zinc-950 border-green-brand"
                    : "bg-card2 text-zinc-400 border-card2-border hover:text-zinc-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Ordens recentes</h2>
          <button
            type="button"
            data-testid="orders-see-all"
            onClick={() => setFilter("todos")}
            className="inline-flex items-center gap-1 text-xs font-semibold text-green-neon hover:text-green-brand"
          >
            Ver todas <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-green-brand" />
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="orders-empty" className="rounded-2xl border border-dashed border-card2-border bg-card2/40 p-10 text-center">
          <Car className="h-10 w-10 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400 font-medium">Nenhuma ordem encontrada.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((o) => {
            const meta = STATUS_META[o.status] || STATUS_META.parado;
            const total = o.budget?.total;
            const num = o.order_number != null ? String(o.order_number) : o.id?.slice(-6).toUpperCase();
            return (
              <Link
                key={o.id}
                to={`/owner/ordens/${o.id}`}
                data-testid={`order-card-${o.id}`}
                className="group rounded-2xl border border-card2-border bg-card2 hover:border-green-brand/60 transition-colors p-4 flex items-center gap-4"
              >
                <div className={`h-12 w-12 rounded-2xl border flex items-center justify-center shrink-0 ${meta.tint}`}>
                  <Car className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-base font-bold text-zinc-100 truncate" translate="no">
                        {[o.vehicle?.make, o.vehicle?.model].filter(Boolean).join(" ") || "Veículo"}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5" translate="no">
                        <span className="font-semibold text-zinc-400">{o.vehicle?.plate || "—"}</span>
                        {num ? ` · OS #${num}` : ""}
                      </div>
                      {o.mechanic_name && (
                        <div data-testid={`order-card-mech-${o.id}`} className="text-[11px] text-amber-300 mt-0.5 inline-flex items-center gap-1" translate="no">
                          <Wrench className="h-3 w-3" /> {o.mechanic_name}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={o.status} short />
                  </div>
                  {total != null && (
                    <div className="mt-1 font-display text-sm font-bold text-green-neon">{brl(total)}</div>
                  )}
                  {o.expected_at && (
                    <div data-testid={`order-card-expected-${o.id}`} className={`mt-1 text-[11px] flex items-center gap-1 ${isExpectedOverdue(o) ? "text-red-400 font-semibold" : "text-zinc-400"}`}>
                      <CalendarClock className="h-3 w-3" />
                      {isExpectedOverdue(o) ? "Atrasada · " : "Previsão: "}{fmtExpected(o.expected_at)}
                    </div>
                  )}
                  {o.checklist_pending && <span data-testid={`order-card-checklist-${o.id}`} className="mt-1 inline-block rounded-full border border-violet-500 bg-violet-500/20 px-2 py-0.5 text-[10px] font-bold text-violet-200">Checklist pendente</span>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
